import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { useCustomer } from "@/context/CustomerContext";
import { custApi, apiError } from "@/lib/api";
import { money, timeStr, dateStr, fmtEvWhen } from "@/components/mc/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { ShoppingCart, Loader2, Clock, Award, LogOut, Package, CalendarDays, FileText, Gift } from "lucide-react";

function StatusBadge({ status }) {
  const map = { new: "bg-primary text-white", preparing: "bg-[#FFBD2E] text-black", ready: "bg-[hsl(var(--success))] text-white", completed: "bg-secondary", cancelled: "bg-accent text-white" };
  return <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 ${map[status] || "bg-secondary"}`}>{status}</span>;
}

const BOOKING_STATUS = {
  requested: { label: "Requested", cls: "bg-[#FFBD2E] text-black" },
  contract_sent: { label: "Contract sent", cls: "bg-indigo-600 text-white" },
  signed: { label: "Signed", cls: "bg-sky-600 text-white" },
  confirmed: { label: "Confirmed", cls: "bg-[hsl(var(--success))] text-white" },
  completed: { label: "Completed", cls: "bg-secondary" },
  cancelled: { label: "Cancelled", cls: "bg-accent text-white" },
};
function EventStatusBadge({ status }) {
  const s = BOOKING_STATUS[status] || { label: status, cls: "bg-secondary" };
  return <span className={`font-mono text-[10px] uppercase px-1.5 py-0.5 ${s.cls}`}>{s.label}</span>;
}

function AuthCard({ slug }) {
  const { login, register } = useCustomer();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [f, setF] = useState({ email: "", password: "", name: "", phone: "" });
  const [loginByEmail, setLoginByEmail] = useState(false);
  const [err, setErr] = useState(""); const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setLoading(true);
    try {
      // Email-first router: business owners / staff go to MorrAccess SSO; customers stay here.
      const emailInPlay = f.email && (mode === "register" || mode === "magic" || mode === "forgot" || (mode === "login" && loginByEmail));
      if (emailInPlay) {
        try {
          const { data: r } = await custApi.get(`/public/auth/route`, { params: { email: f.email } });
          if (r.destination === "sso") { navigate("/sso/redirect"); return; }
        } catch (_) { /* if the check fails, fall through to the customer flow */ }
      }
      if (mode === "login") await login(slug, loginByEmail ? f.email : f.phone, f.password);
      else if (mode === "register") await register(slug, f);
      else if (mode === "forgot") {
        await custApi.post(`/t/${slug}/customer/password/forgot`, { email: f.email });
        setSent(true);
      }
      else if (mode === "magic") {
        await custApi.post(`/t/${slug}/customer/magic-link`, { phone: f.phone, email: f.email });
        setSent(true);
      }
    } catch (er) { setErr(apiError(er.response?.data?.detail) || er.message); }
    finally { setLoading(false); }
  };

  const title = mode === "login" ? "Welcome back" : mode === "register" ? "Create account" : mode === "magic" ? "Sign in with a link" : "Reset password";
  return (
    <div className="max-w-sm mx-auto mt-16 border border-border p-8 bg-card">
      <h2 className="font-display font-extrabold tracking-tighter text-2xl">{title}</h2>
      <p className="text-sm text-muted-foreground mt-1">Store: <span className="font-mono">{slug}</span></p>
      {mode === "forgot" && sent ? (
        <div className="mt-6">
          <p className="text-sm" data-testid="forgot-sent-msg">If an account exists for <b>{f.email}</b>, we've sent a password reset link. Check your inbox.</p>
          <button className="mt-4 text-sm text-primary hover:underline" onClick={() => { setMode("login"); setSent(false); }} data-testid="back-to-login">Back to sign in</button>
        </div>
      ) : mode === "magic" && sent ? (
        <div className="mt-6">
          <p className="text-sm" data-testid="magic-sent-msg">If your phone and email match an account, we've emailed a one-time sign-in link. Open it to sign in.</p>
          <button className="mt-4 text-sm text-primary hover:underline" onClick={() => { setMode("login"); setSent(false); }} data-testid="back-to-login">Back to sign in</button>
        </div>
      ) : (
        <>
          <form onSubmit={submit} className="mt-6 space-y-3" data-testid="customer-auth-form">
            {mode === "register" && <>
              <div><Label>Name</Label><Input className="rounded-none mt-1" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="customer-name-input" /></div>
              <div><Label>Phone <span className="text-muted-foreground normal-case">(your account number)</span></Label><Input type="tel" className="rounded-none mt-1" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="(415) 555-2671" required data-testid="customer-phone-input" /></div>
            </>}
            {mode === "magic" && <div><Label>Phone <span className="text-muted-foreground normal-case">(your account number)</span></Label><Input type="tel" className="rounded-none mt-1" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="(415) 555-2671" required data-testid="magic-phone-input" /></div>}
            {mode === "login" && !loginByEmail && <div><Label>Account number <span className="text-muted-foreground normal-case">(phone)</span></Label><Input type="tel" className="rounded-none mt-1" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="(415) 555-2671" required data-testid="customer-login-phone-input" /></div>}
            {(mode === "register" || mode === "magic" || mode === "forgot" || (mode === "login" && loginByEmail)) &&
              <div><Label>Email</Label><Input type="email" className="rounded-none mt-1" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} required data-testid="customer-email-input" /></div>}
            {(mode === "login" || mode === "register") && <div><Label>Password</Label><Input type="password" className="rounded-none mt-1" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} required data-testid="customer-password-input" /></div>}
            {mode === "login" && <button type="button" className="text-xs text-primary hover:underline text-left" onClick={() => { setLoginByEmail((v) => !v); setErr(""); }} data-testid="login-toggle-identifier">{loginByEmail ? "Use your account number instead" : "Use email instead"}</button>}
            {err && <p className="text-sm text-accent" data-testid="customer-auth-error">{err}</p>}
            <Button type="submit" disabled={loading} className="w-full rounded-none" data-testid="customer-auth-submit">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (mode === "login" ? "Sign in" : mode === "register" ? "Create account" : mode === "magic" ? "Email me a sign-in link" : "Send reset link")}
            </Button>
          </form>
          {mode === "login" && <div className="mt-3 flex flex-col gap-1.5">
            <button className="text-sm text-primary hover:underline text-left" onClick={() => { setMode("magic"); setErr(""); }} data-testid="magic-link-option">Sign in with an email link (no password)</button>
            <button className="text-sm text-muted-foreground hover:text-foreground text-left" onClick={() => { setMode("forgot"); setErr(""); }} data-testid="forgot-password-link">Forgot password?</button>
          </div>}
          {(mode === "magic" || mode === "forgot") && <button className="mt-3 text-sm text-muted-foreground hover:text-foreground block" onClick={() => { setMode("login"); setErr(""); }} data-testid="back-to-login-2">Back to password sign in</button>}
          <button className="mt-2 text-sm text-primary hover:underline" onClick={() => { setMode(mode === "register" ? "login" : "register"); setErr(""); }} data-testid="customer-toggle-mode">
            {mode === "register" ? "Have an account? Sign in" : "Need an account? Register"}
          </button>
        </>
      )}
    </div>
  );
}

export default function CustomerPortal() {
  const { customer, logout, refresh } = useCustomer();
  const [params] = useSearchParams();
  const slug = params.get("store") || "abccoffee";
  const [orders, setOrders] = useState([]);
  const [loyalty, setLoyalty] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [acct, setAcct] = useState({ name: "", phone: "" });

  useEffect(() => {
    if (customer && customer.id) {
      setAcct({ name: customer.name || "", phone: customer.phone || "" });
      custApi.get("/customer/orders").then(({ data }) => setOrders(data)).catch(() => {});
      custApi.get("/customer/bookings").then(({ data }) => setBookings(data)).catch(() => {});
      loadLoyalty();
    }
  }, [customer]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadLoyalty = () => custApi.get("/customer/loyalty").then(({ data }) => setLoyalty(data)).catch(() => {});
  const joinProgram = async (pid) => {
    try { await custApi.post(`/customer/loyalty/${pid}/join`); toast.success("You've joined this program"); loadLoyalty(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (customer === null) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  const Header = (
    <header className="border-b border-border bg-background sticky top-0 z-30">
      <div className="px-4 sm:px-6 h-14 flex items-center justify-between max-w-4xl mx-auto">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-6 w-6 bg-primary flex items-center justify-center"><ShoppingCart className="h-3.5 w-3.5 text-white" /></div>
          <span className="font-display font-extrabold tracking-tighter">MorrCart <span className="text-muted-foreground font-normal text-sm">Portal</span></span>
        </Link>
        {customer && customer.id && <Button variant="ghost" size="sm" onClick={logout} className="rounded-none gap-2" data-testid="customer-logout"><LogOut className="h-4 w-4" /> Sign out</Button>}
      </div>
    </header>
  );

  if (!customer || !customer.id) {
    return <div className="min-h-screen bg-background">{Header}<AuthCard slug={slug} /></div>;
  }

  const saveAcct = async () => {
    try { await custApi.put("/customer/me", acct); toast.success("Account updated"); refresh(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const upcoming = orders.filter((o) => ["new", "preparing", "ready"].includes(o.status));

  return (
    <div className="min-h-screen bg-background text-foreground">
      {Header}
      <main className="px-4 sm:px-6 py-8 max-w-4xl mx-auto">
        <h1 className="font-display font-extrabold tracking-tighter text-2xl sm:text-3xl">Hi, {customer.name?.split(" ")[0] || "there"}</h1>
        <p className="text-sm text-muted-foreground mt-1">Track your orders, pickups, payments and loyalty.</p>

        {customer.email_verified === false && (
          <div className="mt-6 border border-[#FFBD2E] bg-[#FFBD2E]/10 p-4 flex items-center justify-between gap-3" data-testid="verify-email-banner">
            <div className="text-sm"><b>Verify your email</b> to secure your account and rewards. Check your inbox for the link.</div>
            <Button size="sm" variant="outline" className="rounded-none shrink-0" onClick={async () => { try { await custApi.post("/customer/verify-email/request"); toast.success("Verification email sent"); } catch (e) { toast.error(apiError(e.response?.data?.detail)); } }} data-testid="resend-verify-btn">Resend</Button>
          </div>
        )}

        {upcoming.length > 0 && (
          <div className="mt-6 border border-primary bg-primary/5 p-4" data-testid="portal-upcoming">
            <div className="flex items-center gap-2 text-sm font-medium text-primary"><Clock className="h-4 w-4" /> {upcoming.length} active order{upcoming.length > 1 ? "s" : ""}</div>
            {upcoming.map((o) => (
              <div key={o.id} className="mt-2 flex items-center justify-between text-sm">
                <span className="font-mono">#{o.order_number}</span>
                <StatusBadge status={o.status} />
                <span className="text-muted-foreground">Ready ~{timeStr(o.estimated_ready_at)}</span>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="orders" className="mt-8">
          <TabsList className="rounded-none bg-secondary border border-border">
            <TabsTrigger value="orders" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="portal-tab-orders">Orders</TabsTrigger>
            <TabsTrigger value="loyalty" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="portal-tab-loyalty">Loyalty</TabsTrigger>
            <TabsTrigger value="events" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="portal-tab-events">Events</TabsTrigger>
            <TabsTrigger value="account" className="rounded-none data-[state=active]:bg-primary data-[state=active]:text-white" data-testid="portal-tab-account">Account</TabsTrigger>
          </TabsList>

          <TabsContent value="orders" className="mt-6 space-y-3">
            {orders.map((o) => (
              <div key={o.id} className="border border-border p-4" data-testid={`portal-order-${o.order_number}`}>
                <div className="flex items-center justify-between">
                  <span className="font-mono text-sm">#{o.order_number}</span>
                  <StatusBadge status={o.status} />
                </div>
                <div className="mt-2 space-y-0.5">
                  {o.items.map((it, i) => <div key={i} className="flex justify-between text-sm text-muted-foreground"><span>{it.qty}× {it.name}</span><span className="font-mono">{money(it.price * it.qty)}</span></div>)}
                </div>
                <div className="mt-2 flex items-center justify-between text-sm border-t border-border pt-2">
                  <span className="text-muted-foreground">{o.fulfillment === "pickup_later" ? `Scheduled ${dateStr(o.scheduled_at)}` : `Ready ~${timeStr(o.estimated_ready_at)}`}</span>
                  <span className="font-mono font-bold">{money(o.total)}</span>
                </div>
                <div className="mt-1 text-xs text-muted-foreground">Payment: {o.payment_status === "paid" ? "Paid" : "Pay in person (cash)"}</div>
              </div>
            ))}
            {!orders.length && <div className="text-center py-12 text-muted-foreground"><Package className="h-8 w-8 mx-auto mb-2 opacity-40" />No orders yet.</div>}
          </TabsContent>

          <TabsContent value="loyalty" className="mt-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-3xl">
              {(loyalty?.memberships || []).map((m) => {
                const pct = Math.min(100, (m.progress / Math.max(1, m.goal)) * 100);
                return (
                  <div key={m.program_id} className="border border-border p-6 text-white" style={{ background: `linear-gradient(135deg, ${m.accent || "#0033A0"}, #001a52)` }} data-testid={`portal-membership-${m.program_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2 text-sm opacity-90"><Award className="h-4 w-4" /> {m.title}</div>
                      {m.color === "red" && <span className="text-[10px] uppercase font-mono bg-white/20 px-1.5 py-0.5">Expiring soon</span>}
                    </div>
                    <div className="mt-4 font-mono font-bold text-4xl" data-testid={`portal-progress-${m.program_id}`}>{m.progress}<span className="text-lg opacity-70">/{m.goal}</span></div>
                    <div className="text-xs uppercase tracking-wider opacity-70">{m.earn_type === "stamps" ? "stamps" : "points"}</div>
                    <div className="mt-3 h-2 bg-white/20 relative">
                      <div className="h-full bg-white" style={{ width: `${pct}%` }} />
                      {(m.tiers || []).map((t, i) => {
                        const left = Math.min(100, (t.goal / Math.max(1, m.goal)) * 100);
                        const reached = m.progress >= t.goal;
                        return <div key={i} className="absolute -top-1 w-1 h-4" title={`${t.goal}: ${t.reward}`} style={{ left: `calc(${left}% - 2px)`, background: reached ? "#fff" : "rgba(255,255,255,.5)" }} />;
                      })}
                    </div>
                    <div className="mt-3 space-y-1">
                      {(m.tiers || [{ goal: m.goal, reward: m.reward }]).map((t, i) => (
                        <div key={i} className={`flex items-center gap-2 text-xs ${m.progress >= t.goal ? "opacity-100" : "opacity-60"}`} data-testid={`portal-tier-${m.program_id}-${i}`}>
                          <span className="font-mono w-8 text-right">{t.goal}</span>
                          <Gift className="h-3 w-3" />
                          <span className="truncate">{t.reward}</span>
                          {m.progress >= t.goal && <span className="ml-auto text-[10px] uppercase">reached</span>}
                        </div>
                      ))}
                    </div>
                    {(m.ready_rewards || []).length > 0 ? (
                      <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`portal-reward-ready-${m.program_id}`}>
                        {m.ready_rewards.map((rr, i) => (
                          <span key={i} className="text-xs font-bold bg-white/20 px-2 py-1">🎁 {rr.reward} — show at counter</span>
                        ))}
                      </div>
                    ) : m.rewards_ready > 0 ? (
                      <div className="mt-3 text-sm font-bold bg-white/20 px-2 py-1 inline-block" data-testid={`portal-reward-ready-${m.program_id}`}>🎁 {m.rewards_ready} reward{m.rewards_ready > 1 ? "s" : ""} ready — show at counter</div>
                    ) : null}
                    {m.description && <p className="mt-4 text-xs opacity-70">{m.description}</p>}
                  </div>
                );
              })}
            </div>
            {(loyalty?.available || []).length > 0 && (
              <div className="mt-8 max-w-3xl">
                <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">Join a program</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {loyalty.available.map((a) => (
                    <div key={a.program_id} className="border border-border p-5 bg-card" data-testid={`portal-available-${a.program_id}`}>
                      <div className="flex items-center gap-2"><span className="h-3 w-3" style={{ backgroundColor: a.accent }} /><span className="font-display font-bold">{a.title}</span></div>
                      <div className="text-xs text-muted-foreground mt-1">{a.reward} · {a.goal} {a.earn_type === "stamps" ? "stamps" : "points"}</div>
                      {a.description && <p className="text-xs text-muted-foreground mt-2">{a.description}</p>}
                      <Button size="sm" className="rounded-none mt-3" onClick={() => joinProgram(a.program_id)} data-testid={`portal-join-${a.program_id}`}>Join</Button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {!(loyalty?.memberships || []).length && !(loyalty?.available || []).length && (
              <div className="text-sm text-muted-foreground">No loyalty programs available yet.</div>
            )}
          </TabsContent>

          <TabsContent value="events" className="mt-6 space-y-3">
            {bookings.map((b) => (
              <div key={b.id} className="border border-border p-4" data-testid={`portal-booking-${b.confirmation_ref || b.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-sm font-medium"><CalendarDays className="h-4 w-4 text-primary" /> {b.package_name || "Event"}</div>
                  <EventStatusBadge status={b.status} />
                </div>
                <div className="mt-2 text-sm text-muted-foreground">
                  {fmtEvWhen(b.date, b.time, b.timezone)}
                  {b.event_type ? ` · ${b.event_type}` : ""}{b.guest_count ? ` · ${b.guest_count} guests` : ""}
                </div>
                {(b.addons || []).length > 0 && (
                  <div className="mt-1 text-xs text-muted-foreground">Add-ons: {b.addons.map((a) => a.name).join(", ")}</div>
                )}
                <div className="mt-2 flex items-center justify-between text-sm border-t border-border pt-2">
                  <span className="font-mono text-xs text-muted-foreground">{b.confirmation_ref || ""}</span>
                  {b.estimated_total ? <span className="font-mono font-bold">{money(b.estimated_total)}</span> : <span />}
                </div>
                {b.status === "requested" && <div className="mt-1 text-xs text-muted-foreground">Your date isn’t locked in yet — we’ll email you to confirm.</div>}
                {b.status === "contract_sent" && <div className="mt-1 text-xs text-muted-foreground">A contract has been emailed to you — please review and sign to lock in your date.</div>}
                {b.contract_pdf_url && (
                  <a href={b.contract_pdf_url} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1.5 text-xs font-medium text-primary hover:underline" data-testid={`portal-booking-contract-${b.confirmation_ref || b.id}`}><FileText className="h-3.5 w-3.5" /> View signed contract (PDF)</a>
                )}
              </div>
            ))}
            {!bookings.length && <div className="text-center py-12 text-muted-foreground" data-testid="portal-no-bookings"><CalendarDays className="h-8 w-8 mx-auto mb-2 opacity-40" />No event bookings yet.</div>}
          </TabsContent>

          <TabsContent value="account" className="mt-6">
            <div className="max-w-sm space-y-4 border border-border p-6">
              <div><Label>Email</Label><Input disabled className="rounded-none mt-1" value={customer.email} /></div>
              <div><Label>Name</Label><Input className="rounded-none mt-1" value={acct.name} onChange={(e) => setAcct({ ...acct, name: e.target.value })} data-testid="account-name-input" /></div>
              <div><Label>Phone</Label><Input className="rounded-none mt-1" value={acct.phone} onChange={(e) => setAcct({ ...acct, phone: e.target.value })} data-testid="account-phone-input" /></div>
              <Button onClick={saveAcct} className="rounded-none" data-testid="save-account-btn">Save changes</Button>
            </div>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
